import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import { StickyNote, Plus, Search, Paperclip, Trash2, Download } from "lucide-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Hoc from "../../components/HOC";
import Loader from "../../components/Loader";
import ModalComponent from "../../components/Modal";
import CustomDropdown from "../../components/CustomDropdown";
import {
  fetchNotes,
  getNoteById,
  
  createNote,
  updateNote,
  deleteNote,
  deleteNoteAttachment,
  downloadNoteAttachment,
} from "../../Services/notesApi";

const emptyForm = { title: "", content: "" };
const stickyToneClasses = [
  "bg-[#EBFEFF] border-[#C2F1FF]",
  "bg-[#C2F1FF] border-[#BBD5ED]",
  "bg-[#BBD5ED] border-[#A8CDF0]",
  "bg-[#A8CDF0] border-[#9CBFE7]",
  "bg-[#9CBFE7] border-[#BBD5ED]",
];

const getStickyTone = (noteId) => {
  const seed = Number(noteId) || 0;
  return stickyToneClasses[Math.abs(seed) % stickyToneClasses.length];
};

const getStickyTilt = (noteId) => {
  const seed = Number(noteId) || 0;
  const tilts = ["-rotate-[0.6deg]", "rotate-[0.4deg]", "-rotate-[0.3deg]", "rotate-[0.8deg]"];
  return tilts[Math.abs(seed) % tilts.length];
};

const NotesContent = () => {
  const [notes, setNotes] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const { notes: rows, totalElements: total } = await fetchNotes({
        page,
        size: rowsPerPage,
        search: debouncedSearch,
      });
      setNotes(rows);
      setTotalElements(total);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not load notes.";
      toast.error(msg);
      setNotes([]);
      setTotalElements(0);
    } finally {
      setListLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDetail(null);
    setPendingFiles([]);
    setModalOpen(true);
  };

  const openEdit = async (note) => {
    setEditingId(note.id);
    setForm({
      title: note.title ?? "",
      content: note.content ?? "",
    });
    setPendingFiles([]);
    setModalOpen(true);
    setModalLoading(true);
    try {
      const full = await getNoteById(note.id);
      setDetail(full);
      setForm({
        title: full.title ?? "",
        content: full.content ?? "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not load note.";
      toast.error(msg);
      setDetail(note);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setDetail(null);
    setPendingFiles([]);
    setForm(emptyForm);
  };

  const refreshDetail = async (id) => {
    if (!id) return;
    const full = await getNoteById(id);
    setDetail(full);
  };

  const maxFileSize = 3 * 1024 * 1024; // 3MB

  

  const validateForm = () => {
    if (!form.title?.trim() || !form.content?.trim()) {
      toast.error("Please fill title and content to save the note.");
      return;
    }

    const oversized = pendingFiles.find((f) => f && f.size > maxFileSize);
    if (oversized) {
      toast.error(`Each file must be at most ${maxFileSize / (1024 * 1024)} MB.`);
      return;
    }
    return true;
  }

  const handleSave = async () => {
    if(!validateForm()) return;

    setModalLoading(true);
    try {
      if (editingId) {
        await updateNote(editingId, form, pendingFiles);
        toast.success("Note updated successfully.");
      } else {
        await createNote(form, pendingFiles);
        toast.success("Note saved successfully.");
      }
      setPendingFiles([]);
      
     
      loadList();
      closeModal();
      return;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not save note."
      );
    } finally {
      setModalLoading(false);
    }
  };

  //delete note

  const handleDeleteNote = async (note) => {
    const res = await Swal.fire({
      title: "Delete note?",
      text: note.title || "This note will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    try {
      await deleteNote(note.id);
      toast.success("Note deleted.");
      if (editingId === note.id) closeModal();
      loadList();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not delete note.";
      toast.error(msg);
    }
  };


  const handleRemovePending = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!editingId) return;
    const res = await Swal.fire({
      title: "Remove attachment?",
      text: attachment.fileName,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Remove",
    });
    if (!res.isConfirmed) return;
    try {
      await deleteNoteAttachment(editingId, attachment.id);
      toast.success("Attachment removed.");
      await refreshDetail(editingId);
      loadList();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not remove attachment.";
      toast.error(msg);
    }
  };

  const handleDownload = async (attachment) => {
    if (!editingId) return;
    try {
      await downloadNoteAttachment(editingId, attachment);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Download failed.";
      toast.error(msg);
    }
  };
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (file.size > maxFileSize) {
        toast.error(`File ${file.name} size should be less than 3MB`);
        return;
      }
    });
    setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = "";
};

  const attachments = detail?.attachments ?? [];

  const currentPage = page + 1;
  const totalPageCount = Math.ceil(totalElements / rowsPerPage) || 0;

  const handleChangePage = (newPage) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (nextSize) => {
    setRowsPerPage(nextSize);
    setPage(0);
  };

  const getPaginationInfo = () => {
    if (totalElements === 0) return { start: 0, end: 0 };
    const start = page * rowsPerPage + 1;
    const end = Math.min((page + 1) * rowsPerPage, totalElements);
    return { start, end };
  };

  const getPageNumbers = () => {
    const count = totalPageCount;
    if (count <= 5) {
      return Array.from({ length: count }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5];
    }
    if (currentPage >= count - 2) {
      return [
        count - 4,
        count - 3,
        count - 2,
        count - 1,
        count,
      ];
    }
    return [
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
    ];
  };

  const { start, end } = getPaginationInfo();
  const pageNumbers = getPageNumbers();

  return (
    <div className="bg-white flex-1 min-h-0">
      <Loader isLoading={listLoading && notes.length === 0} />

      <div className="p-6 w-full bg-white">
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative flex-1 max-w-xl">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="search"
                  placeholder="Search notes by title or content"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="block w-full pl-10 pr-4 py-2 rounded-[4px] border border-[#D1D1D1] bg-white text-sm text-[#201F1E] placeholder-[#656464] focus:outline-none  transition-colors duration-150"
                />
              </div>
              <div className="flex-1 flex justify-end">
              <button
                type="button"
                onClick={openNew}
                className="px-4 py-2 bg-[#201F1E] text-white rounded-lg  hover:bg-[#201F1E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#201F1E] flex items-center text-sm font-semibold whitespace-nowrap"
              >
                New note
              </button>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6 min-h-[280px]">
            {!listLoading && notes.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 px-4 rounded-lg border border-dashed border-gray-200 bg-gray-50/80 text-center">
                <StickyNote className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-700">
                  No notes yet
                </p>
                <p className="text-sm text-gray-500 mt-1 max-w-sm">
                  {debouncedSearch
                    ? "Try a different search, or clear the filter to see all notes."
                    : "Create your first note with the button above."}
                </p>
              </div>
            )}

            {notes.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                <AnimatePresence mode="popLayout">
                  {notes.map((note) => (
                    <motion.article
                      key={note.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.2 }}
                      className={`group rounded-md p-5 shadow-sm transition-all cursor-pointer flex flex-col min-h-[240px] max-h-[240px] overflow-hidden ${getStickyTone(
                        note.id
                      )} ${getStickyTilt(
                        note.id
                      )} hover:rotate-0 hover:shadow-lg hover:-translate-y-0.5 focus-within:ring-2 focus-within:ring-[#9CBFE7]`}
                      onClick={() => openEdit(note)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openEdit(note);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2 shrink-0">
                        <h2 className="font-semibold text-gray-900 line-clamp-2 pr-2 min-h-[2.5rem] text-sm sm:text-base">
                          {note.title?.trim() || "Untitled"}
                        </h2>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNote(note);
                          }}
                          className="shrink-0 p-1.5 rounded-lg text-gray-900 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                          aria-label="Delete note"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-800 line-clamp-4 flex-1 min-h-0 overflow-hidden leading-5">
                        {(note.content ?? "").length > 300
                          ? `${(note.content ?? "").substring(0, 300)}...`
                          : note.content ?? ""}
                      </p>
                      <div className="flex items-center justify-between mt-auto pt-3 shrink-0 border-t border-black/10 text-xs text-gray-900">
                        <span>
                          {note.updatedAt
                            ? moment(note.updatedAt).fromNow()
                            : "—"}
                        </span>
                        {(note.attachments?.length > 0 ||
                          Number(note.attachmentCount) > 0) && (
                          <span className="inline-flex items-center gap-1 text-gray-900 font-medium">
                            <Paperclip className="w-3.5 h-3.5 text-gray-900" />
                            {note?.attachmentCount ?? 0}
                          </span>
                        )}
                      </div>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                type="button"
                onClick={() => currentPage > 1 && handleChangePage(1)}
                disabled={currentPage === 1 || totalElements === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                type="button"
                onClick={() =>
                  currentPage > 1 && handleChangePage(currentPage - 1)
                }
                disabled={currentPage === 1 || totalElements === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  currentPage < totalPageCount &&
                  handleChangePage(currentPage + 1)
                }
                disabled={currentPage === totalPageCount || totalElements === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() =>
                  currentPage < totalPageCount &&
                  handleChangePage(totalPageCount)
                }
                disabled={currentPage === totalPageCount || totalElements === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">{start}</span> to{" "}
                  <span className="font-medium">{end}</span> of{" "}
                  <span className="font-medium">{totalElements}</span> notes
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor="notes-rows-per-page"
                    className="text-sm text-gray-700"
                  >
                    Rows per page:
                  </label>
                  <select
                    id="notes-rows-per-page"
                    value={rowsPerPage}
                    onChange={(e) =>
                      handleChangeRowsPerPage(Number(e.target.value))
                    }
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#9CBFE7] focus:border-[#9CBFE7]"
                  >
                    <option value={6}>6</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                    <option value={48}>48</option>
                  </select>
                </div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    type="button"
                    onClick={() => currentPage > 1 && handleChangePage(1)}
                    disabled={currentPage === 1 || totalElements === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      currentPage > 1 && handleChangePage(currentPage - 1)
                    }
                    disabled={currentPage === 1 || totalElements === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {pageNumbers.map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => handleChangePage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium hover:bg-gray-50 ${
                        pageNum === currentPage
                          ? "bg-blue-50 text-blue-600"
                          : "bg-white text-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      currentPage < totalPageCount &&
                      handleChangePage(currentPage + 1)
                    }
                    disabled={
                      currentPage === totalPageCount || totalElements === 0
                    }
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      currentPage < totalPageCount &&
                      handleChangePage(totalPageCount)
                    }
                    disabled={
                      currentPage === totalPageCount || totalElements === 0
                    }
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalComponent
        show={modalOpen}
        onClose={closeModal}
        title={editingId ? "Edit note" : "Add note"}
        classes="w-full max-w-2xl"
      >
        <div className="mt-4 space-y-4 overflow-y-auto max-h-[80vh] ">
          {modalLoading && !form.title && !form.content && editingId ? (
            <div className="py-12 text-center text-slate-500 text-sm">Loading…</div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                  required
                  maxLength={255}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#9CBFE7]/25 focus:border-[#9CBFE7] outline-none"
                />
                <div className="text-xs text-slate-500 text-right">
                  {form.title?.length ?? 0}/255 characters
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Content
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, content: e.target.value }))
                  }
                  required
                 
                  rows={10}
                  
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#9CBFE7]/25 focus:border-[#9CBFE7] outline-none resize-none min-h-[200px] text-slate-800"
                />
                
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Attachments
                </label>
                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#9CBFE7] hover:bg-[#9CBFE7]/10 transition-colors">
                  <Paperclip className="w-6 h-6 text-slate-400 mb-1" />
                  <span className="text-sm text-slate-600">
                    Click to add files
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
                {pendingFiles.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-slate-600">
                    {pendingFiles.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between gap-2 bg-[#9CBFE7]/10 rounded-lg px-3 py-2 "
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          className="text-red-600 text-xs font-medium shrink-0"
                          onClick={() => handleRemovePending(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {editingId && attachments.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {attachments.map((att) => (
                      <li
                        key={att.id}
                        className="flex items-center justify-between gap-2 bg-[#9CBFE7]/10 rounded-lg px-3 py-2 text-sm"
                      >
                        <span className="truncate text-gray-700">
                          {att.fileName}
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleDownload(att)}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-[#9CBFE7] hover:bg-[#9CBFE7]/10"
                            aria-label="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(att)}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50"
                            aria-label="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={modalLoading || !form.title.trim() || !form.content.trim()}
                  onClick={handleSave}
                  className="px-5 py-2.5 rounded-xl bg-[#201F1E] text-white font-semibold hover:bg-[#201F1E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#201F1E] disabled:opacity-60"
                >
                  {modalLoading ? "Saving…" : "Save"}
                </button>
              </div>
            </>
          )}
        </div>
      </ModalComponent>
    </div>
  );
};

export default Hoc(NotesContent);
